"use client";
import { usePathname } from "next/navigation";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import {
  initialClients,
  initialTasks,
  type Client,
  type Task,
} from "@/Helpers/WorkspaceData";
import {
  messages,
  type Locale,
  type Localized,
  type MessageKey,
  formatMoney,
  formatDate,
} from "@/libs/localization/i18n";
export type Activity = { id: number; text: Localized; time: string };
type Workspace = {
  locale: Locale;
  t: (key: MessageKey) => string;
  l: (value: Localized) => string;
  money: (cents: number) => string;
  date: (value: string, options?: Intl.DateTimeFormatOptions) => string;
  tasks: Task[];
  completeTasks: (ids: string[]) => void;
  addTask: (task: Task) => void;
  clients: Client[];
  addClient: (client: Client) => void;
  activity: Activity[];
  log: (text: Localized) => void;
  notice: Localized | null;
  notify: (text: Localized | null) => void;
  paymentStatus: Record<string, "approved" | "rejected">;
  setPaymentStatus: (ids: string[], status: "approved" | "rejected") => void;
  archived: string[];
  archive: (ids: string[]) => void;
};
const Context = createContext<Workspace | null>(null);
export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const locale: Locale = pathname.split("/")[1] === "en" ? "en" : "nl";
  const [tasks, setTasks] = useState(initialTasks);
  const [clients, setClients] = useState(initialClients);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [notice, setNotice] = useState<Localized | null>(null);
  const [paymentStatus, setStatuses] = useState<Workspace["paymentStatus"]>({});
  const [archived, setArchived] = useState<string[]>([]);
  const notify = useCallback((text: Localized | null) => setNotice(text), []);
  const log = useCallback(
    (text: Localized) => {
      setActivity((a) => [
        {
          id: Date.now() + Math.random(),
          text,
          time: new Date().toISOString(),
        },
        ...a,
      ]);
      notify(messages.localSaved);
    },
    [notify],
  );
  function completeTasks(ids: string[]) {
    setTasks((ts) => ts.filter((t) => !ids.includes(t.id)));
    log({
      nl: `${ids.length} voorstel(len) verwerkt in de demo`,
      en: `${ids.length} proposal(s) applied in the demo`,
    });
    notify(messages.localApproved);
  }
  const value: Workspace = {
    locale,
    t: (key) => messages[key][locale],
    l: (text) => text[locale],
    money: (cents) => formatMoney(locale, cents),
    date: (value, options) => formatDate(locale, value, options),
    tasks,
    completeTasks,
    addTask: (task) => {
      setTasks((ts) => [task, ...ts]);
      log(task.title);
    },
    clients,
    addClient: (client) => {
      setClients((cs) => [client, ...cs]);
      log({
        nl: `Dossier ${client.id} aangemaakt`,
        en: `Case file ${client.id} created`,
      });
    },
    activity,
    log,
    notice,
    notify,
    paymentStatus,
    setPaymentStatus: (ids, status) => {
      setStatuses((s) => ({
        ...s,
        ...Object.fromEntries(ids.map((id) => [id, status])),
      }));
      log({
        nl: `${ids.length} demobetaling(en) ${status === "approved" ? "geaccordeerd" : "afgewezen"}`,
        en: `${ids.length} demo payment(s) ${status}`,
      });
    },
    archived,
    archive: (ids) => {
      setArchived((a) => [...a, ...ids]);
      log({
        nl: `${ids.length} document(en) gearchiveerd`,
        en: `${ids.length} document(s) archived`,
      });
    },
  };
  return <Context.Provider value={value}>{children}</Context.Provider>;
}
export function useWorkspace() {
  const context = useContext(Context);
  if (!context) throw new Error("WorkspaceProvider is required");
  return context;
}
